import React from 'react';
import classnames from 'classnames';
import {history} from 'client/history';

import Checkbox from '../../../atoms/Checkbox/Checkbox.react';
import './data-table.scss';

import DataTableCell from './DataTableCell';
import type {DataTableRowProps} from './DataTable.types';

/**
 * Renders a single `<tr>` for DataTable.
 *
 * Supports:
 * - Row selection (checkbox in first cell)
 * - Click-to-navigate (via `rowLinkFunc`)
 * - Generic row click handler (via `onRowClick`)
 * - Selected-row highlighting
 *
 * Uses the existing `o-table__row*` CSS classes.
 */
function DataTableRow<TData>({
  row,
  onRowClick,
  rowLinkFunc,
  enableRowSelection
}: DataTableRowProps<TData>) {
  const isSelected = row.getIsSelected();
  const href = rowLinkFunc ? rowLinkFunc(row.original) : undefined;
  const isLink = !!href || !!onRowClick;

  const handleClick = (_e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      onRowClick(row.original);
    } else if (href) {
      history.push(href);
    }
  };

  return (
    <tr
      className={classnames('o-table__row', {
        'o-table__row--selected': isSelected,
        'o-table__row--link': isLink
      })}
      data-testid={`o-table-row-${row.index}`}
      {...(href ? {href, role: 'link'} : {})}
      {...(isLink ? {onClick: handleClick} : {})}
    >
      {enableRowSelection && (
        <td className='o-table__cell o-table__selection-cell' onClick={(e) => e.stopPropagation()}>
          <Checkbox
            label={`Select row ${row.index + 1}`}
            className='o-table__row-select'
            checked={isSelected}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
            size='s'
          />
        </td>
      )}

      {row.getVisibleCells().map((cell) => (
        <DataTableCell key={cell.id} cell={cell} />
      ))}
    </tr>
  );
}

export default DataTableRow;
